import { ChevronLeft, LogOut, UserRound } from 'lucide-react';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthContext } from '../../../context/AuthContext';
import { ForgeTheme } from '../../hooks/useForgeTheme';
import { ForgeContextSwitcher } from './ForgeContextSwitcher';
import { ThemeToggle } from './ThemeToggle';

/**
 * The footer of a workspace sidebar (Archon, Lurker): the tool and environment switcher, the
 * theme toggle, Profile, Sign out and Hide. The workspaces share it, so one cannot drift from the
 * others' behaviour.
 */
export const WorkspaceSidebarFooter: React.FC<{
  theme: ForgeTheme;
  toggleTheme: () => void;
  /** The desktop rail is collapsed; never on mobile. */
  collapsed: boolean;
  isMobile: boolean;
  /** Takes the sidebar off the page; the layout's reveal handle brings it back. */
  hideSidebar: () => void;
  onMobileNavigate?: () => void;
}> = ({ theme, toggleTheme, collapsed, isMobile, hideSidebar, onMobileNavigate }) => {
  const { logout } = useAuthContext();
  const navigate = useNavigate();
  return (
    <div className="sidebar-footer">
      <div className="sidebar-footer-utilities">
        <ForgeContextSwitcher variant={isMobile ? 'drawer' : 'sidebar'} />
        <ThemeToggle theme={theme} onToggle={toggleTheme} variant="sidebar" collapsed={collapsed} />
        <button
          className="sidebar-footer-link"
          onClick={() => {
            onMobileNavigate?.();
            navigate('/forge/profile');
          }}
          type="button"
        >
          <UserRound aria-hidden="true" />
          <span className="sidebar-collapse-label">Profile</span>
        </button>
        <button
          className="sidebar-footer-link"
          onClick={() => {
            onMobileNavigate?.();
            logout();
            navigate('/stores');
          }}
          type="button"
        >
          <LogOut aria-hidden="true" />
          <span className="sidebar-collapse-label">Sign out</span>
        </button>
      </div>
      {!isMobile ? (
        <>
          <div className="sidebar-footer-divider" />
          <button
            className="sidebar-collapse-btn"
            onClick={hideSidebar}
            type="button"
            aria-label="Hide navigation"
          >
            <ChevronLeft aria-hidden="true" />
            <span className="sidebar-collapse-label">Hide</span>
          </button>
        </>
      ) : null}
    </div>
  );
};
